import { TILE_HALF_W } from '@enugu/shared';
import { proximityGain, screenPan } from './proximity';
export class PositionalAudio {
    ctx = null;
    sources = new Map();
    lx = 0;
    ly = 0;
    /** Half the visible width in world px: full left/right pan at the screen edge. */
    halfViewPx = 400;
    master = 1;
    /** Browsers only allow audio after a user gesture: call from a tap handler. */
    unlock() {
        if (!this.ctx) {
            const Ctor = window.AudioContext ?? window.webkitAudioContext;
            if (!Ctor)
                return;
            this.ctx = new Ctor();
        }
        if (this.ctx.state === 'suspended')
            void this.ctx.resume();
    }
    get ready() {
        return !!this.ctx && this.ctx.state === 'running';
    }
    setListener(x, y) {
        this.lx = x;
        this.ly = y;
    }
    /** Attaches any audio node graph as a positional source. `build` connects its output to `input`. */
    attachNode(id, x, y, params, build) {
        if (!this.ctx)
            return null;
        this.detach(id);
        const gain = this.ctx.createGain();
        gain.gain.value = 0;
        const pan = this.ctx.createStereoPanner();
        gain.connect(pan).connect(this.ctx.destination);
        const src = { id, x, y, params, volume: 1, gain, pan };
        src.stop = build(this.ctx, gain);
        this.sources.set(id, src);
        return src;
    }
    /** M3 voice hook: a remote participant's MediaStream at an entity's position. */
    attachStream(id, stream, x, y, params) {
        return this.attachNode(id, x, y, params, (ctx, input) => {
            const node = ctx.createMediaStreamSource(stream);
            node.connect(input);
            return () => node.disconnect();
        });
    }
    setSourcePosition(id, x, y) {
        const s = this.sources.get(id);
        if (s) {
            s.x = x;
            s.y = y;
        }
    }
    detach(id) {
        const s = this.sources.get(id);
        if (!s)
            return;
        s.stop?.();
        s.gain.disconnect();
        s.pan.disconnect();
        this.sources.delete(id);
    }
    /** Per render frame (cheap: a few AudioParam ramps per source). */
    update() {
        if (!this.ctx)
            return;
        const t = this.ctx.currentTime;
        for (const s of this.sources.values()) {
            const dx = s.x - this.lx;
            const dy = s.y - this.ly;
            const g = proximityGain(Math.hypot(dx, dy), s.params) * s.volume * this.master;
            // Screen-space x offset of the source: (dx - dy) * 32 px.
            const p = screenPan((dx - dy) * TILE_HALF_W, this.halfViewPx);
            s.gain.gain.setTargetAtTime(g, t, 0.05);
            s.pan.pan.setTargetAtTime(p, t, 0.05);
        }
    }
}
